import type { AgentTeam } from "@albertine/types/openapi/lmt";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { singularPlural } from "../../common/utils/time.formatted.util";

type TeamRow = {
    id?: string;
    rowKey: string;
    name: string;
    memberCount: number;
};

type Props = {
    open: boolean;
    onClose: () => void;
    teams: AgentTeam[];
    isSaving: boolean;
    onSave: (teams: { id?: string; name: string }[]) => void;
};

export function EditTeamsDialog({ open, onClose, teams, isSaving, onSave }: Props) {
    const [rows, setRows] = useState<TeamRow[]>(() => teamsToRows(teams));
    const [focusKey, setFocusKey] = useState<string | null>(null);

    function addTeam() {
        const rowKey = crypto.randomUUID();
        setRows((curr) => [...curr, { rowKey, name: "", memberCount: 0 }]);
        setFocusKey(rowKey);
    }

    function renameTeam(rowKey: string, name: string) {
        setRows((curr) =>
            curr.map((team) => (team.rowKey === rowKey ? { ...team, name } : team)),
        );
    }

    function removeTeam(rowKey: string) {
        setRows((curr) => curr.filter((team) => team.rowKey !== rowKey));
    }

    const persistedIds = new Set(rows.flatMap((team) => (team.id ? [team.id] : [])));
    const unassignedByRemoval = teams
        .filter((team) => !persistedIds.has(team.id))
        .reduce((count, team) => count + team.agentIds.length, 0);

    const hasBlankName = rows.some((team) => !team.name.trim());

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent size="small" className="bg-white" showCloseButton={false}>
                <DialogHeader>
                    <DialogTitle className="heading-4 text-zinc-900">
                        Edit teams
                    </DialogTitle>
                    <DialogDescription className="text-sm text-zinc-500 leading-body-sm">
                        Add, rename or remove teams. Assign agents to a team from the
                        agents list.
                    </DialogDescription>
                </DialogHeader>

                {rows.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 py-6">
                        <p className="text-sm text-zinc-400 leading-body-sm">
                            No teams yet.
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={addTeam}
                            className="w-fit"
                        >
                            <Plus />
                            New team
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col gap-2">
                            {rows.map((team) => (
                                <div
                                    key={team.rowKey}
                                    className="flex items-center gap-2"
                                >
                                    <Input
                                        value={team.name}
                                        autoFocus={team.rowKey === focusKey}
                                        placeholder="Team name"
                                        aria-label="Team name"
                                        onChange={(e) =>
                                            renameTeam(
                                                team.rowKey,
                                                e.currentTarget.value,
                                            )
                                        }
                                    />
                                    <span className="w-16 shrink-0 text-right text-xs text-zinc-400 tabular-nums leading-4">
                                        {singularPlural(
                                            `${team.memberCount} agent`,
                                            `${team.memberCount} agents`,
                                            team.memberCount,
                                        )}
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        aria-label={`Remove ${team.name || "team"}`}
                                        onClick={() => removeTeam(team.rowKey)}
                                        className="shrink-0 text-zinc-400 hover:text-red-600"
                                    >
                                        <Trash2 />
                                    </Button>
                                </div>
                            ))}
                        </div>

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={addTeam}
                            className="mt-4 w-fit"
                        >
                            <Plus />
                            New team
                        </Button>
                    </>
                )}

                {unassignedByRemoval > 0 && (
                    <p className="mt-3 text-xs text-zinc-500 leading-4">
                        {singularPlural(
                            "1 agent will become unassigned.",
                            `${unassignedByRemoval} agents will become unassigned.`,
                            unassignedByRemoval,
                        )}
                    </p>
                )}

                <Separator className="my-4" />

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <SaveButton
                        disabled={hasBlankName || isSaving}
                        onClick={() =>
                            onSave(
                                rows.map((team) => ({
                                    ...(team.id ? { id: team.id } : {}),
                                    name: team.name.trim(),
                                })),
                            )
                        }
                    />
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function teamsToRows(teams: AgentTeam[]): TeamRow[] {
    return teams.map((team) => ({
        id: team.id,
        rowKey: team.id,
        name: team.name,
        memberCount: team.agentIds.length,
    }));
}

function SaveButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
    const button = (
        <Button disabled={disabled} onClick={onClick}>
            Save
        </Button>
    );

    if (!disabled) return button;

    return (
        <TooltipProvider delay={200}>
            <Tooltip>
                <TooltipTrigger
                    render={<span className="inline-block">{button}</span>}
                />
                <TooltipContent positionerClassName="z-[600]">
                    Team needs a name
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
